import {
  EuiBasicTable,
  EuiCheckbox,
  EuiFlexGroup,
  EuiFormRow,
  EuiTableComputedColumnType,
  EuiToken,
} from "@inscopix/ideas-eui";
import { useDataTableContext } from "pages/gdt/store/DataTableProvider";
import {
  DataTableColumnDefinition,
  DataTableStoreState,
} from "pages/gdt/store/DataTableProvider.types";
import { COLUMN_KIND_MAP } from "../ModalCreateColumn/ModalCreateColumn";
import { useCallback, useMemo } from "react";
import { keyBy } from "lodash";
import { isNonNullish } from "utils/isNonNullish";

export type FieldColumnMultiProps = {
  autoFocus?: boolean;
  tableId: string;
  label?: string;
  includeColumn: (
    column: DataTableStoreState["tables"][number]["columns"][number],
  ) => void;
  isColumnAlwaysSelected?: (column: {
    id: string;
    name: string;
    isIdColumn: boolean;
    kind: DataTableColumnDefinition["kind"];
  }) => boolean;
  value: string[];
  onChange: (columnIds: string[]) => void;
};

export const FieldColumnMulti = ({
  autoFocus = false,
  tableId,
  label = "Columns",
  includeColumn,
  isColumnAlwaysSelected,
  value,
  onChange,
}: FieldColumnMultiProps) => {
  const items = useDataTableContext((s) => {
    const table = s.tables.find((table) => table.id === tableId);
    const groupsById = keyBy(table?.columnGroups ?? [], (group) => group.id);
    return (table?.columns ?? []).filter(includeColumn).map((column) => ({
      id: column.id,
      name: column.name,
      groupName: isNonNullish(column.group)
        ? groupsById[column.group].name
        : undefined,
      isIdColumn: column.is_id_column,
      kind: column.definition.kind,
    }));
  });

  const isAllSelected = useMemo(() => {
    const itemIds = new Set(value);
    return items
      .filter((item) => !isColumnAlwaysSelected?.(item))
      .every((item) => itemIds.has(item.id));
  }, [isColumnAlwaysSelected, items, value]);

  const handleSelectAll = useCallback(() => {
    const itemIds = items
      .filter((item) => !isColumnAlwaysSelected?.(item))
      .map((item) => item.id);
    onChange(itemIds);
  }, [isColumnAlwaysSelected, items, onChange]);

  const handleDeselectAll = useCallback(() => {
    onChange([]);
  }, [onChange]);

  const columns: EuiTableComputedColumnType<{
    id: string;
    name: string;
    groupName: string | undefined;
    isSelected: boolean;
    isDisabled: boolean;
    onSelect: () => void;
    onDeselect: () => void;
    kind: DataTableColumnDefinition["kind"];
  }>[] = useMemo(() => {
    return [
      {
        name: (
          <EuiCheckbox
            id="select_all"
            checked={isAllSelected}
            onChange={(e) => {
              if (e.target.checked) {
                handleSelectAll();
              } else {
                handleDeselectAll();
              }
            }}
          />
        ),
        width: "32px",
        render: ({ id, isSelected, isDisabled, onSelect, onDeselect }) => {
          return (
            <EuiCheckbox
              id={id}
              disabled={isDisabled}
              checked={isDisabled || isSelected}
              title={
                isDisabled
                  ? "This column is required for table linking"
                  : "Select this column"
              }
              onChange={(e) => {
                if (e.target.checked) {
                  onSelect();
                } else {
                  onDeselect();
                }
              }}
            />
          );
        },
      },
      {
        name: "Name",
        render: ({ name, groupName }) => {
          if (groupName === undefined) {
            return name;
          } else {
            return (
              <span>
                <span style={{ color: "grey" }}>
                  {groupName} {">"}
                </span>{" "}
                {name}
              </span>
            );
          }
        },
      },
      {
        name: "Type",
        width: "30%",
        render: ({ kind }) => {
          return (
            <EuiFlexGroup alignItems="center" gutterSize="s">
              <EuiToken iconType={COLUMN_KIND_MAP[kind].icon} />
              <span>{COLUMN_KIND_MAP[kind].name}</span>
            </EuiFlexGroup>
          );
        },
      },
    ];
  }, [handleDeselectAll, handleSelectAll, isAllSelected]);

  return (
    <EuiFormRow fullWidth label={label}>
      <EuiBasicTable
        autoFocus={autoFocus}
        columns={columns}
        itemId={(item) => item.id}
        items={items.map((item) => ({
          ...item,
          isDisabled: isColumnAlwaysSelected?.(item) ?? false,
          isSelected: value.includes(item.id),
          onSelect: () => onChange([...value, item.id]),
          onDeselect: () => onChange(value.filter((id) => id !== item.id)),
        }))}
      />
    </EuiFormRow>
  );
};
